/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DealTracker from '../components/DealTracker';
import OfferManagement from '../components/OfferManagement';
import OffersGrouped from '../components/OffersGrouped';
import ContractViewer from '../components/ContractViewer';
import MLSCoordination from '../components/MLSCoordination';
import CreateListingWizard from '../components/CreateListingWizard';
import LogOfferWizard from '../components/LogOfferWizard';
import VerificationBanner from '../components/VerificationBanner';
import VerificationGate from '../components/VerificationGate';
import PropertyCard from '../components/PropertyCard';
import PropertyCardSkeleton from '../components/PropertyCardSkeleton';
import './Portal.css';
import '../components/VerificationComponents.css';

function SellerPortal({ user }) {
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [collapsedOfferGroups, setCollapsedOfferGroups] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [soldTransactions, setSoldTransactions] = useState([]);
  const [loadingSold, setLoadingSold] = useState(true);
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);
  
  // KYC STATE - Read from localStorage first to get fresh data after verification redirect
  const getStoredUser = () => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };
  const storedUser = getStoredUser();
  const [accountTier, setAccountTier] = useState(
    storedUser?.accountTier || user?.accountTier || 'unverified'
  );
  
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '');
    if (['listings', 'offers', 'transactions', 'sold'].includes(hash)) {
      return hash;
    }
    return 'listings';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };
  const [showLogOfferForm, setShowLogOfferForm] = useState(false);
  const [offerFormData, setOfferFormData] = useState({
    listingId: '', buyerName: '', buyerEmail: '', buyerAgent: '', buyerAgentEmail: '',
    offerAmount: '', earnestMoney: '', financingType: 'conventional', closingDate: '', notes: ''
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [showMLSModal, setShowMLSModal] = useState(false);
  const [showContractsModal, setShowContractsModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAIReviewModal, setShowAIReviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingListing, setDeletingListing] = useState(false);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewResult, setAiReviewResult] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formImages, setFormImages] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [savingDraft, setSavingDraft] = useState(false);
  const [editingListingId, setEditingListingId] = useState(null);
  const [editingListingStatus, setEditingListingStatus] = useState(null);
  const [editingPaymentStatus, setEditingPaymentStatus] = useState(null);
  const [counterModalOffer, setCounterModalOffer] = useState(null);
  const [acceptModalOffer, setAcceptModalOffer] = useState(null);
  const [rejectModalOffer, setRejectModalOffer] = useState(null);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [counterClosingDate, setCounterClosingDate] = useState('');
  const [counterEarnestMoney, setCounterEarnestMoney] = useState('');
  const [counterConcessions, setCounterConcessions] = useState('');
  const [processingOffer, setProcessingOffer] = useState(false);
  const [copyToast, setCopyToast] = useState(null);
  
  const initialFormData = {
    // Listing Details
    propertyType: 'single_family',
    listingType: 'standard',
    listingDuration: '6',
    buyerAgentCommission: '',
    title: '',
    description: '',
    // Location
    address: '',
    city: '',
    state: '',
    zipCode: '',
    county: '',
    legalDescription: '',
    schoolDistrict: '',
    // Pricing
    price: '',
    hoaFees: '',
    hoaFrequency: '',
    taxAmount: '',
    // Property Details
    bedrooms: '',
    fullBathrooms: '',
    halfBathrooms: '',
    garageSpaces: '',
    squareFeet: '',
    lotSize: '',
    yearBuilt: '',
    stories: '1',
    construction: 'brick',
    roofType: 'composition',
    // Property Features
    heating: 'central',
    cooling: 'central',
    flooring: '',
    pool: 'none',
    appliances: [],
    otherInclusions: '',
    exclusions: '',
    // Listing Terms
    financingAccepted: ['cash', 'conventional'],
    possession: 'at_closing',
    propertyCondition: 'good',
    // Showing Instructions
    showingType: 'lockbox',
    lockboxLocation: '',
    contactPhone: '',
    contactEmail: '',
    showingInstructions: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);

  const fetchUserStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // API returns { user: { accountTier, kycStatus, ... } }
      const userData = response.data.user;
      
      if (userData && userData.accountTier) {
        setAccountTier(userData.accountTier);
        
        // Update localStorage with fresh data from server
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.accountTier = userData.accountTier;
        storedUser.kycStatus = userData.kycStatus;
        localStorage.setItem('user', JSON.stringify(storedUser));
      }
    } catch (error) {
      console.error('Error fetching user status:', error);
    }
  };

  useEffect(() => {
    const initializePortal = async () => {
      // Check for payment success redirect from Stripe FIRST
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      const listingId = urlParams.get('listing_id');
      
      if (paymentStatus === 'success' && listingId) {
        // Payment successful - verify and open wizard at DocuSign step
        try {
          const token = localStorage.getItem('token');
          if (token) {
            // First try to mark as paid
            try {
              await axios.post(`/api/listings/mark-paid/${listingId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              console.log('Payment marked as complete');
            } catch (markError) {
              console.log('Mark-paid failed, trying Stripe verification...');
              // If mark-paid fails, verify directly with Stripe as backup
              try {
                const verifyResponse = await axios.post(`/api/listings/verify-payment/${listingId}`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (verifyResponse.data.paid) {
                  console.log('Payment verified via Stripe API');
                } else {
                  console.error('Payment not found in Stripe');
                }
              } catch (verifyError) {
                console.error('Stripe verification also failed:', verifyError);
              }
            }
            
            // Set up the wizard to open at DocuSign step regardless
            setEditingListingId(parseInt(listingId));
            setEditingPaymentStatus('paid');
            setShowCreateForm(true);
          } else {
            // No token - user got logged out during Stripe redirect
            console.error('No auth token found after Stripe redirect');
            // Store the listing ID so we can recover after login
            localStorage.setItem('pendingPaymentListingId', listingId);
          }
          // Clear URL params
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error handling payment success:', error);
          // Still try to open the wizard
          setEditingListingId(parseInt(listingId));
          setEditingPaymentStatus('paid');
          setShowCreateForm(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else if (paymentStatus === 'cancelled' && listingId) {
        // Payment cancelled - reopen wizard at payment step
        setEditingListingId(parseInt(listingId));
        setEditingPaymentStatus('pending');
        setShowCreateForm(true);
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      // Check if there's a pending payment from a previous session
      const pendingListingId = localStorage.getItem('pendingPaymentListingId');
      if (pendingListingId) {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const verifyResponse = await axios.post(`/api/listings/verify-payment/${pendingListingId}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (verifyResponse.data.paid) {
              console.log('Recovered pending payment for listing', pendingListingId);
              setEditingListingId(parseInt(pendingListingId));
              setEditingPaymentStatus('paid');
              setShowCreateForm(true);
            }
          } catch (e) {
            console.error('Failed to recover pending payment:', e);
          }
          localStorage.removeItem('pendingPaymentListingId');
        }
      }
      
      // Now fetch all data
      fetchMyListings();
      fetchMyTransactions();
      fetchSoldTransactions();
      fetchAllOffers();
      fetchUserStatus();
    };
    
    initializePortal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyListings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/listings/seller/my-listings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoadingListings(false);
    }
  };

  const fetchMyTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/dealtracker/user/' + user.id + '/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const fetchSoldTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/dealtracker/user/' + user.id + '/completed', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSoldTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching sold transactions:', error);
    } finally {
      setLoadingSold(false);
    }
  };

  const fetchAllOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/offers/seller/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
      setOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  };

  const handleOfferFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'offerAmount' || name === 'earnestMoney') {
      setOfferFormData({ ...offerFormData, [name]: value.replace(/[^0-9]/g, '') });
    } else {
      setOfferFormData({ ...offerFormData, [name]: value });
    }
  };

  const handleLogOffer = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/offers/external', {
        listing_id: offerFormData.listingId,
        buyer_name: offerFormData.buyerName,
        buyer_email: offerFormData.buyerEmail,
        buyer_agent: offerFormData.buyerAgent,
        buyer_agent_email: offerFormData.buyerAgentEmail,
        offer_amount: offerFormData.offerAmount,
        earnest_money: offerFormData.earnestMoney,
        financing_type: offerFormData.financingType,
        closing_date: offerFormData.closingDate,
        notes: offerFormData.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowLogOfferForm(false);
      setOfferFormData({ listingId: '', buyerName: '', buyerEmail: '', buyerAgent: '', buyerAgentEmail: '',
        offerAmount: '', earnestMoney: '', financingType: 'conventional', closingDate: '', notes: '' });
      fetchAllOffers();
      fetchMyListings();
    } catch (error) {
      console.error('Error logging offer:', error);
      alert('Failed to log offer');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
    
    // Format price with commas
    if (name === 'price' || name === 'hoaFees' || name === 'taxAmount') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData({
        ...formData,
        [name]: numericValue
      });
      return;
    }
    
    // Handle commission - allow decimal
    if (name === 'buyerAgentCommission') {
      const numericValue = value.replace(/[^0-9.]/g, '');
      setFormData({
        ...formData,
        [name]: numericValue
      });
      return;
    }
    
    // Format phone number
    if (name === 'contactPhone') {
      const numericValue = value.replace(/[^0-9]/g, '');
      let formatted = numericValue;
      if (numericValue.length >= 6) {
        formatted = `(${numericValue.slice(0,3)}) ${numericValue.slice(3,6)}-${numericValue.slice(6,10)}`;
      } else if (numericValue.length >= 3) {
        formatted = `(${numericValue.slice(0,3)}) ${numericValue.slice(3)}`;
      }
      setFormData({
        ...formData,
        [name]: formatted
      });
      return;
    }
    
    // Reset listing type if state changes from TX
    if (name === 'state' && value !== 'TX' && formData.listingType === 'seller_finance') {
      setFormData({
        ...formData,
        [name]: value,
        listingType: 'standard'
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCheckboxChange = (name, value) => {
    const currentValues = formData[name] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    setFormData({
      ...formData,
      [name]: newValues
    });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setFormImages([...formImages, ...newImages]);
  };

  const removeFormImage = (index) => {
    const newImages = [...formImages];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setFormImages(newImages);
  };

  const formatPrice = (value) => {
    if (!value) return '';
    return Number(value).toLocaleString();
  };

  const validateForm = () => {
    const errors = {};
    const required = ['title', 'description', 'address', 'city', 'state', 'zipCode', 'price', 'bedrooms', 'fullBathrooms', 'squareFeet', 'yearBuilt', 'buyerAgentCommission', 'contactPhone', 'contactEmail'];
    
    required.forEach(field => {
      if (!formData[field]) {
        errors[field] = 'This field is required';
      }
    });
    
    if (formData.contactEmail && !/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingListingId) {
        // Check if this is a draft that needs payment
        const isDraft = editingListingStatus === 'draft';
        const isUnpaid = editingPaymentStatus !== 'paid';
        
        if (isDraft && isUnpaid) {
          // Draft listing needs payment - update it first, then redirect to Stripe
          await axios.put(`/api/listings/${editingListingId}`, {
            ...formData,
            status: 'draft'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Upload images if any
          if (formImages.length > 0) {
            for (const img of formImages) {
              const imageFormData = new FormData();
              imageFormData.append('image', img.file);
              await axios.post(`/api/listings/${editingListingId}/images`, imageFormData, {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              });
            }
          }
          
          // Create Stripe checkout session for this listing
          const response = await axios.post('/api/listings/create-checkout-session', {
            listingData: formData,
            existingListingId: editingListingId
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Redirect to Stripe checkout
          if (response.data.url) {
            window.location.href = response.data.url;
          }
        } else {
          // Already paid listing - just update it
          await axios.put(`/api/listings/${editingListingId}`, {
            title: formData.title,
            description: formData.description,
            price: formData.price,
            bedrooms: formData.bedrooms,
            bathrooms: formData.fullBathrooms,
            squareFeet: formData.squareFeet,
            lotSize: formData.lotSize,
            status: 'active'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Upload images if any
          if (formImages.length > 0) {
            for (const img of formImages) {
              const imageFormData = new FormData();
              imageFormData.append('image', img.file);
              await axios.post(`/api/listings/${editingListingId}/images`, imageFormData, {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              });
            }
          }
          
          setShowCreateForm(false);
          setFormData(initialFormData);
          setFormImages([]);
          setFormErrors({});
          setEditingListingId(null);
          setEditingListingStatus(null);
          setEditingPaymentStatus(null);
          fetchMyListings();
        }
      } else {
        // Create new listing - redirect to Stripe checkout
        const response = await axios.post('/api/listings/create-checkout-session', {
          listingData: formData
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Redirect to Stripe checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      console.error('Error saving listing:', error);
      alert('Failed to save listing');
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/listings/draft', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateForm(false);
      setFormData(initialFormData);
      setEditingListingId(null);
      setEditingListingStatus(null);
      setEditingPaymentStatus(null);
      fetchMyListings();
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setSavingDraft(false);
    }
  };

  const loadListingForEdit = (listing) => {
    setFormData({
      // Address
      street: listing.address || '',
      city: listing.city || '',
      state: listing.state || 'TX',
      zipCode: listing.zip_code || '',
      county: listing.county || '',
      
      // Type
      propertyType: listing.property_type || 'single_family',
      
      // Details
      yearBuilt: listing.year_built ? String(listing.year_built) : '',
      bedrooms: listing.bedrooms ? String(listing.bedrooms) : '',
      bathrooms: listing.bathrooms ? String(listing.bathrooms) : '',
      squareFeet: listing.square_feet ? String(listing.square_feet) : '',
      lotSize: listing.lot_size ? String(listing.lot_size) : '',
      stories: listing.stories ? String(listing.stories) : '',
      garageSpaces: listing.garage_spaces ? String(listing.garage_spaces) : '',
      pool: listing.pool || '',
      
      // Districts
      hasHOA: !!listing.has_hoa,
      inMUD: !!listing.in_mud,
      inPID: !!listing.in_pid,
      isCoastal: !!listing.is_coastal,
      inFloodZone: !!listing.in_flood_zone,
      
      // Lead Paint Details
      leadPaintKnown: listing.lead_paint_known || '',
      leadPaintRecords: listing.lead_paint_records || '',
      leadPaintRecordsDetails: listing.lead_paint_records_details || '',
      
      // HOA Details
      hoaName: listing.hoa_name || '',
      hoaFee: listing.hoa_fee ? String(listing.hoa_fee) : '',
      hoaPhone: listing.hoa_phone || '',
      hoaEmail: listing.hoa_email || '',
      hoaTransferFee: listing.hoa_transfer_fee || '',
      hoaSpecialAssessments: listing.hoa_special_assessments || '',
      
      // MUD Details
      mudName: listing.mud_name || '',
      mudTaxRate: listing.mud_tax_rate || '',
      mudPhone: listing.mud_phone || '',
      
      // PID Details
      pidName: listing.pid_name || '',
      pidTaxRate: listing.pid_tax_rate || '',
      pidPhone: listing.pid_phone || '',
      
      // Coastal Details
      coastalDuneProtection: !!listing.coastal_dune_protection,
      coastalBeachAccess: listing.coastal_beach_access || '',
      coastalNotes: listing.coastal_notes || '',
      
      // Flood Zone Details
      floodZoneType: listing.flood_zone_type || '',
      floodInsuranceRequired: !!listing.flood_insurance_required,
      floodHistory: listing.flood_history || '',
      floodHistoryDetails: listing.flood_history_details || '',
      
      // TOS
      agreedToTOS: !!listing.agreed_to_tos,
      
      // Pricing
      price: listing.price ? String(listing.price) : '',
      buyerAgentCommission: listing.buyer_agent_commission ? String(listing.buyer_agent_commission) : '',
      
      // Photos
      photos: listing.images || [],
      
      // Description
      title: listing.title || '',
      description: listing.description || '',
      
      // Current step (for resuming drafts)
      currentStep: listing.current_step || 1,
    });
    setEditingListingId(listing.id);
    setEditingListingStatus(listing.status);
    setEditingPaymentStatus(listing.payment_status);
    setShowCreateForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/listings/${selectedListing.id}/images`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Image uploaded successfully!');
      fetchMyListings();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOfferAccepted = (transactionId) => {
    setShowOffersModal(false);
    fetchMyListings();
    fetchMyTransactions();
    fetchAllOffers();
    handleTabChange('transactions');
  };

  const handleAcceptOffer = async (offerId) => {
    // Find the offer and open the accept modal
    const offer = offers.find(o => o.id === offerId);
    if (offer) {
      setAcceptModalOffer(offer);
    }
  };

  const confirmAcceptOffer = async () => {
    if (!acceptModalOffer) return;
    setProcessingOffer(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/offers/${acceptModalOffer.id}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAcceptModalOffer(null);
      fetchAllOffers();
      fetchMyTransactions();
      handleTabChange('transactions');
    } catch (error) {
      alert(error.response?.data?.error || 'Error accepting offer');
    } finally {
      setProcessingOffer(false);
    }
  };

  const handleRejectOffer = async (offerId) => {
    // Find the offer and open the reject modal
    const offer = offers.find(o => o.id === offerId);
    if (offer) {
      setRejectModalOffer(offer);
    }
  };

  const confirmRejectOffer = async () => {
    if (!rejectModalOffer) return;
    setProcessingOffer(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/offers/${rejectModalOffer.id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRejectModalOffer(null);
      fetchAllOffers();
    } catch (error) {
      alert(error.response?.data?.error || 'Error rejecting offer');
    } finally {
      setProcessingOffer(false);
    }
  };

  const handleCounterOffer = async () => {
    if (!counterModalOffer) return;
    setProcessingOffer(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/offers/${counterModalOffer.id}/counter`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCounterModalOffer(null);
      setCounterAmount('');
      setCounterMessage('');
      setCounterClosingDate('');
      setCounterEarnestMoney('');
      setCounterConcessions('');
      fetchAllOffers();
    } catch (error) {
      alert(error.response?.data?.error || 'Error marking offer as countered');
    } finally {
      setProcessingOffer(false);
    }
  };

  const handleDeleteListing = async () => {
    if (!selectedListing) return;
    setDeletingListing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/listings/${selectedListing.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      setSelectedListing(null);
      fetchMyListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      alert('Failed to delete listing');
    } finally {
      setDeletingListing(false);
    }
  };

  const handleAIReview = async () => {
    if (!selectedListing) return;
    setAiReviewLoading(true);
    setAiReviewResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/ai-review/listing/${selectedListing.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiReviewResult(response.data.review);
    } catch (error) {
      console.error('Error getting AI review:', error);
      setAiReviewResult('Unable to generate AI review at this time. Please try again later.');
    } finally {
      setAiReviewLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      pending: 'badge-warning',
      sold: 'badge-info',
      withdrawn: 'badge-secondary'
    };
    return badges[status] || 'badge-secondary';
  };

  // Handle wizard submission - redirects to Stripe
  const handleWizardSubmit = async (listingData, photos) => {
    console.log('handleWizardSubmit called with:', { listingData, photos, editingListingId, editingPaymentStatus });
    try {
      const token = localStorage.getItem('token');
      
      // Use draftId from wizard if available, otherwise use editingListingId
      const existingId = listingData.draftId || editingListingId;
      
      // If editing an already-paid listing (active/pending), just update it
      if (editingListingId && editingPaymentStatus === 'paid') {
        console.log('Updating paid listing', editingListingId);
        const response = await axios.put(`/api/listings/${editingListingId}`, listingData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Update response:', response.data);
        
        // Wait for listings to refresh before closing
        await fetchMyListings();
        
        setShowCreateForm(false);
        setEditingListingId(null);
        setEditingListingStatus(null);
        setEditingPaymentStatus(null);
        setFormData(initialFormData);
        return;
      }
      
      // For drafts (unpaid) or new listings - redirect to Stripe checkout
      const response = await axios.post('/api/listings/create-checkout-session', {
        listingData: listingData,
        existingListingId: existingId // Use draft ID from wizard or editing ID
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error saving listing:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  // Filter for active/pending listings (for Log Offer button - pending allows backup offers)
  const activeListings = listings.filter(l => l.status === 'active' || l.status === 'pending');

  return (
    <div className="portal">
      <div className="portal-header">
        <h1>Seller Portal</h1>
      </div>

      {/* VERIFICATION BANNER */}
      <VerificationBanner accountTier={accountTier} />

      {/* Tabs */}
      <div className="portal-tabs-wrapper">
        <div className="portal-tabs">
          <button 
            className={`tab ${activeTab === 'listings' ? 'active' : ''}`}
            onClick={() => handleTabChange('listings')}
          >
            My Listings ({listings.filter(l => l.status !== 'sold').length})
          </button>
          <button 
            className={`tab ${activeTab === 'offers' ? 'active' : ''}`}
            onClick={() => handleTabChange('offers')}
          >
            My Offers ({offers.length})
          </button>
          <button 
            className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => handleTabChange('transactions')}
          >
            Under Contract ({transactions.length})
          </button>
          <button 
            className={`tab ${activeTab === 'sold' ? 'active' : ''}`}
            onClick={() => handleTabChange('sold')}
          >
            Sold ({soldTransactions.length})
          </button>
        </div>
      </div>

      <div className="portal-content">
        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <>
            <div className="button-header">
              <VerificationGate 
                feature="create_listing"
                onVerified={() => setShowCreateForm(true)}
              >
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    if (showCreateForm) {
                      setShowCreateForm(false);
                      setEditingListingId(null);
                      setEditingListingStatus(null);
                      setEditingPaymentStatus(null);
                      setFormData(initialFormData);
                      setFormErrors({});
                    } else {
                      // Reset all editing state when creating new listing
                      setEditingListingId(null);
                      setEditingListingStatus(null);
                      setEditingPaymentStatus(null);
                      setFormData(initialFormData);
                      setShowCreateForm(true);
                    }
                  }}
                >
                  {showCreateForm ? 'Cancel' : '+ Create New Listing'}
                </button>
              </VerificationGate>
            </div>

            {showCreateForm && (
              <CreateListingWizard
                initialData={editingListingId ? formData : null}
                isEditing={editingListingStatus && editingListingStatus !== 'draft'}
                existingDraftId={editingListingId}
                onClose={() => {
                  setShowCreateForm(false);
                  setFormData(initialFormData);
                  setFormImages([]);
                  setEditingListingId(null);
                  setEditingListingStatus(null);
                  setEditingPaymentStatus(null);
                  // Refresh listings to show any saved drafts (small delay to ensure server processed)
                  setTimeout(() => {
                    fetchMyListings();
                  }, 300);
                }}
                onSubmit={handleWizardSubmit}
              />
            )}

            {loadingListings && !showCreateForm ? (
              null
            ) : listings.filter(l => l.status !== 'sold').length === 0 && !showCreateForm ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3>Ready to list your property?</h3>
                  <p>Create your first listing to start receiving offers from buyers.</p>
                </div>
              </div>
            ) : !showCreateForm && (
              <div className="listings-list">
                {listings.filter(l => l.status !== 'sold').map(listing => {
                  // Check if listing has accepted offers or active transactions
                  const hasAcceptedOffer = offers.some(o => o.listing_id === listing.id && o.status === 'accepted');
                  const hasTransaction = transactions.some(t => t.listing_id === listing.id);
                  // Only allow delete for draft listings - not active, pending, or under_contract
                  const canDelete = listing.status === 'draft' && !hasAcceptedOffer && !hasTransaction;
                  
                  return (
                    <PropertyCard
                      key={listing.id}
                      stage="listings"
                      property={listing}
                      status={listing.status || 'active'}
                      actions={[
                        ...(listing.status === 'active' && listing.public_slug
                          ? [{
                              label: 'Copy Offer Link',
                              onClick: () => {
                                const url = `https://liberastro.com/offer/${listing.public_slug}`;
                                navigator.clipboard.writeText(url).then(() => {
                                  setCopyToast(url);
                                  setTimeout(() => setCopyToast(null), 3000);
                                }).catch(() => {
                                  prompt('Copy this link:', url);
                                });
                              }
                            }]
                          : []
                        ),
                        ...(listing.status === 'draft'
                          ? [{ label: 'Edit', onClick: () => loadListingForEdit(listing) }]
                          : []
                        ),
                        ...(canDelete
                          ? [{ label: 'Delete', onClick: () => { setSelectedListing(listing); setShowDeleteModal(true); } }]
                          : []
                        )
                      ]}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <>
            <div className="button-header">
              <div className="log-offer-button-wrapper" title={activeListings.length === 0 ? "Publish a listing first to log offers" : ""}>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowLogOfferForm(true)}
                  disabled={activeListings.length === 0}
                >
                  + Log Offer
                </button>
              </div>
            </div>

            {/* Log Offer Wizard Modal */}
            {showLogOfferForm && (
              <LogOfferWizard
                onClose={() => setShowLogOfferForm(false)}
                onSubmit={async (formData) => {
                  const token = localStorage.getItem('token');
                  await axios.post('/api/offers/external', {
                    listing_id: parseInt(formData.listingId),
                    buyer_name: formData.buyerName,
                    buyer_email: formData.buyerEmail,
                    buyer_phone: formData.buyerPhone,
                    buyer_agent: formData.buyerAgent,
                    buyer_agent_email: formData.buyerAgentEmail,
                    offer_amount: parseFloat(formData.offerAmount),
                    earnest_money: formData.earnestMoney ? parseFloat(formData.earnestMoney) : null,
                    financing_type: formData.financingType,
                    closing_date: formData.closingDate || null,
                    contingencies: formData.contingencies,
                    notes: formData.notes
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  fetchAllOffers();
                  fetchMyListings();
                }}
                listings={listings}
              />
            )}

            {loadingOffers ? (
              null
            ) : offers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3>No offers yet</h3>
                  <p>Manually log offers you receive to track and compare them.</p>
                </div>
              </div>
            ) : (
              <OffersGrouped
                offers={offers}
                listings={listings}
                onAccept={handleAcceptOffer}
                onCounter={(offer) => setCounterModalOffer(offer)}
                onReject={handleRejectOffer}
                onAIReview={(listing) => { setSelectedListing(listing); setShowAIReviewModal(true); }}
                processingOffer={processingOffer}
                collapsedListings={collapsedOfferGroups}
                setCollapsedListings={setCollapsedOfferGroups}
              />
            )}
          </>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <>
            {loadingTransactions ? (
              null
            ) : transactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                    </svg>
                  </div>
                  <h3>No contracts yet</h3>
                  <p>Accepted offers move here so you can track progress to closing.</p>
                </div>
              </div>
            ) : (
              <div className="transactions-list">
                {transactions.map(transaction => {
                  const listing = listings.find(l => l.id === transaction.listing_id) || {};
                  // Find the accepted offer for this transaction to get buyer details
                  const relatedOffer = offers.find(o => o.listing_id === transaction.listing_id && o.status === 'accepted') || {};
                  return (
                    <PropertyCard
                      key={transaction.id}
                      stage="under_contract"
                      property={{
                        address: transaction.address,
                        city: transaction.city,
                        state: transaction.state,
                        zip_code: transaction.zip_code,
                        images: listing.images,
                        bedrooms: listing.bedrooms,
                        bathrooms: listing.bathrooms,
                        square_feet: listing.square_feet,
                        property_type: listing.property_type
                      }}
                      status="under_contract"
                      transaction={{
                        sale_price: transaction.sale_price,
                        closing_date: transaction.closing_date,
                        buyer_name: transaction.buyer_name || relatedOffer.buyer_name || `${relatedOffer.buyer_first_name || ''} ${relatedOffer.buyer_last_name || ''}`.trim(),
                        financing_type: transaction.financing_type || relatedOffer.financing_type,
                        earnest_money: transaction.earnest_money || relatedOffer.earnest_money
                      }}
                    >
                      <DealTracker 
                        transactionId={transaction.id} 
                        transaction={transaction} 
                        userType="seller" 
                        onStepComplete={() => {
                          fetchMyTransactions();
                          fetchSoldTransactions();
                          fetchMyListings();
                        }}
                      />
                    </PropertyCard>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Sold Tab */}
        {activeTab === 'sold' && (
          <>
            {loadingSold ? (
              null
            ) : soldTransactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3>No completed sales yet</h3>
                  <p>When a transaction closes, it will appear here.</p>
                </div>
              </div>
            ) : (
              <div className="transactions-list">
                {soldTransactions.map(transaction => {
                  const listing = listings.find(l => l.id === transaction.listing_id) || {};
                  return (
                    <PropertyCard
                      key={transaction.id}
                      stage="sold"
                      property={{
                        address: transaction.address,
                        city: transaction.city,
                        state: transaction.state,
                        zip_code: transaction.zip_code,
                        images: listing.images,
                        bedrooms: listing.bedrooms,
                        bathrooms: listing.bathrooms,
                        square_feet: listing.square_feet,
                        property_type: listing.property_type
                      }}
                      status="sold"
                      transaction={{
                        sale_price: transaction.sale_price,
                        closed_at: transaction.closed_at
                      }}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Upload Modal */}
      {showImageModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal-content modal-redesigned" onClick={e => e.stopPropagation()}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <div className="modal-icon modal-icon-sky">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                  </svg>
                </div>
                <h3>Manage Images</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => setShowImageModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {/* Show existing images */}
              {selectedListing.images && selectedListing.images.length > 0 && (
                <div className="existing-images" style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Current Images ({selectedListing.images.length})</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                    {selectedListing.images.map((img, index) => (
                      <div key={img.id || index} style={{ position: 'relative' }}>
                        <img 
                          src={img.image_url} 
                          alt={`Property ${index + 1}`}
                          style={{ width: '120px', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        {img.is_primary === 1 && (
                          <span style={{ 
                            position: 'absolute', 
                            top: '5px', 
                            left: '5px', 
                            background: '#10b981', 
                            color: 'white', 
                            fontSize: '10px', 
                            padding: '2px 6px', 
                            borderRadius: '4px' 
                          }}>Primary</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="image-upload-area">
                <label className="upload-dropzone" style={{
                  display: 'block',
                  border: '2px dashed #d1d5db',
                  borderRadius: '12px',
                  padding: '40px',
                  textAlign: 'center',
                  background: '#f9fafb',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ marginBottom: '16px' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '4px' }}>
                    {uploadingImage ? 'Uploading...' : <>Drag and drop photos here, or <span style={{ color: '#111827', fontWeight: '500', textDecoration: 'underline' }}>browse</span></>}
                  </p>
                  <p style={{ fontSize: '12px', color: '#9ca3af' }}>JPG, PNG, GIF, WebP. Max size: 5MB.</p>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    disabled={uploadingImage}
                  />
                </label>
              </div>
              
              <div style={{ marginTop: '24px' }}>
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%' }}
                  onClick={() => setShowImageModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Offers Modal */}
      {showOffersModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowOffersModal(false)}>
          <div className="modal-content modal-large modal-redesigned" onClick={e => e.stopPropagation()}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <div className="modal-icon modal-icon-emerald">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </div>
                <h3>Offers</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => setShowOffersModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <OfferManagement 
                listingId={selectedListing.id} 
                userType="seller"
                onOfferAccepted={handleOfferAccepted}
              />
              
              <div style={{ marginTop: '24px' }}>
                <button 
                  className="btn btn-primary" 
                  style={{ width: '100%' }}
                  onClick={() => setShowOffersModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MLS Modal */}
      {showMLSModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowMLSModal(false)}>
          <div className="modal-content modal-large modal-redesigned" onClick={e => e.stopPropagation()}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <div className="modal-icon modal-icon-indigo">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>
                  </svg>
                </div>
                <h3>MLS Listing</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => setShowMLSModal(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <MLSCoordination listingId={selectedListing.id} userType="seller" />
            </div>
          </div>
        </div>
      )}

      {/* Contracts Modal */}
      {showContractsModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowContractsModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Contracts</h3>
              <button className="modal-close" onClick={() => setShowContractsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <ContractViewer 
                listingId={selectedListing.id} 
                listingType={selectedListing.listingType || selectedListing.listing_type}
                userType="seller" 
              />
            </div>
          </div>
        </div>
      )}

      {/* Accept Offer Modal */}
      {acceptModalOffer && (
        <div className="modal-overlay" onClick={() => setAcceptModalOffer(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw', borderRadius: '16px' }}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <h3>Accept Offer</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => setAcceptModalOffer(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{padding: '24px'}}>
              {/* Offer Details */}
              <div style={{border: '2px solid #111827', borderRadius: '10px', padding: '20px', marginBottom: '20px', background: 'white'}}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Offer Details</div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px'}}>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Buyer</div>
                    <div style={{fontWeight: '600', color: '#111827', fontSize: '15px'}}>{acceptModalOffer.buyer_name || `${acceptModalOffer.buyer_first_name || ''} ${acceptModalOffer.buyer_last_name || ''}`.trim() || 'Not specified'}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Offer Amount</div>
                    <div style={{fontWeight: '700', color: '#111827', fontSize: '18px'}}>${Number(acceptModalOffer.offer_amount).toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Closing Date</div>
                    <div style={{fontWeight: '600', color: '#111827', fontSize: '15px'}}>{acceptModalOffer.closing_date ? new Date(acceptModalOffer.closing_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : 'Not specified'}</div>
                  </div>
                </div>
              </div>

              {/* Buyer's Agent */}
              <div style={{border: '2px solid #111827', borderRadius: '10px', padding: '20px', background: 'white'}}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Buyer's Agent</div>
                <div style={{marginBottom: '16px'}}>
                  <div style={{fontWeight: '600', color: '#111827', fontSize: '16px', marginBottom: '8px'}}>{acceptModalOffer.buyer_agent || 'Not specified'}</div>
                  {acceptModalOffer.buyer_agent_phone && <div style={{fontSize: '14px', color: '#374151', marginBottom: '4px'}}>{acceptModalOffer.buyer_agent_phone}</div>}
                  {acceptModalOffer.buyer_agent_email && <div style={{fontSize: '14px', color: '#374151'}}>{acceptModalOffer.buyer_agent_email}</div>}
                </div>
                <div style={{fontSize: '14px', color: '#374151', lineHeight: '1.5'}}>
                  Contact the buyer's agent to accept this offer and sign the purchase agreement. If you'd like to negotiate any terms, consider countering instead.
                </div>
              </div>
            </div>
            <div className="modal-footer-redesigned">
              <button className="btn btn-secondary" onClick={() => setAcceptModalOffer(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmAcceptOffer} disabled={processingOffer} style={{backgroundColor: '#111827'}}>
                {processingOffer ? 'Processing...' : 'Mark as Accepted'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Counter Offer Modal */}
      {counterModalOffer && (
        <div className="modal-overlay" onClick={() => { setCounterModalOffer(null); setCounterAmount(''); setCounterMessage(''); setCounterClosingDate(''); setCounterEarnestMoney(''); setCounterConcessions(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw', borderRadius: '16px' }}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <h3>Counter Offer</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => { setCounterModalOffer(null); setCounterAmount(''); setCounterMessage(''); setCounterClosingDate(''); setCounterEarnestMoney(''); setCounterConcessions(''); }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{padding: '24px'}}>
              {/* Offer Details */}
              <div style={{border: '2px solid #111827', borderRadius: '10px', padding: '20px', marginBottom: '20px', background: 'white'}}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Offer Details</div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px'}}>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Buyer</div>
                    <div style={{fontWeight: '600', color: '#111827', fontSize: '15px'}}>{counterModalOffer.buyer_name || `${counterModalOffer.buyer_first_name || ''} ${counterModalOffer.buyer_last_name || ''}`.trim() || 'Not specified'}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Offer Amount</div>
                    <div style={{fontWeight: '700', color: '#111827', fontSize: '18px'}}>${Number(counterModalOffer.offer_amount).toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Closing Date</div>
                    <div style={{fontWeight: '600', color: '#111827', fontSize: '15px'}}>{counterModalOffer.closing_date ? new Date(counterModalOffer.closing_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : 'Not specified'}</div>
                  </div>
                </div>
              </div>

              {/* Buyer's Agent */}
              <div style={{border: '2px solid #111827', borderRadius: '10px', padding: '20px', background: 'white'}}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Buyer's Agent</div>
                <div style={{marginBottom: '16px'}}>
                  <div style={{fontWeight: '600', color: '#111827', fontSize: '16px', marginBottom: '8px'}}>{counterModalOffer.buyer_agent || 'Not specified'}</div>
                  {counterModalOffer.buyer_agent_phone && <div style={{fontSize: '14px', color: '#374151', marginBottom: '4px'}}>{counterModalOffer.buyer_agent_phone}</div>}
                  {counterModalOffer.buyer_agent_email && <div style={{fontSize: '14px', color: '#374151'}}>{counterModalOffer.buyer_agent_email}</div>}
                </div>
                <div style={{fontSize: '14px', color: '#374151', lineHeight: '1.5'}}>
                  Contact the buyer's agent with your counter terms. Once you reach an agreement, they'll send a revised purchase agreement for your signature.
                </div>
              </div>
            </div>
            <div className="modal-footer-redesigned">
              <button className="btn btn-secondary" onClick={() => { setCounterModalOffer(null); setCounterAmount(''); setCounterMessage(''); setCounterClosingDate(''); setCounterEarnestMoney(''); setCounterConcessions(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCounterOffer} style={{backgroundColor: '#111827'}}>
                Mark as Countered
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Offer Modal */}
      {rejectModalOffer && (
        <div className="modal-overlay" onClick={() => setRejectModalOffer(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '500px', maxWidth: '90vw', borderRadius: '16px' }}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <h3>Reject Offer</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => setRejectModalOffer(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{padding: '24px'}}>
              {/* Offer Details */}
              <div style={{border: '2px solid #111827', borderRadius: '10px', padding: '20px', marginBottom: '20px', background: 'white'}}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Offer Details</div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px'}}>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Buyer</div>
                    <div style={{fontWeight: '600', color: '#111827', fontSize: '15px'}}>{rejectModalOffer.buyer_name || `${rejectModalOffer.buyer_first_name || ''} ${rejectModalOffer.buyer_last_name || ''}`.trim() || 'Not specified'}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Offer Amount</div>
                    <div style={{fontWeight: '700', color: '#111827', fontSize: '18px'}}>${Number(rejectModalOffer.offer_amount).toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'}}>Closing Date</div>
                    <div style={{fontWeight: '600', color: '#111827', fontSize: '15px'}}>{rejectModalOffer.closing_date ? new Date(rejectModalOffer.closing_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : 'Not specified'}</div>
                  </div>
                </div>
              </div>

              {/* Buyer's Agent */}
              <div style={{border: '2px solid #111827', borderRadius: '10px', padding: '20px', background: 'white'}}>
                <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>Buyer's Agent</div>
                <div style={{marginBottom: '16px'}}>
                  <div style={{fontWeight: '600', color: '#111827', fontSize: '16px', marginBottom: '8px'}}>{rejectModalOffer.buyer_agent || 'Not specified'}</div>
                  {rejectModalOffer.buyer_agent_phone && <div style={{fontSize: '14px', color: '#374151', marginBottom: '4px'}}>{rejectModalOffer.buyer_agent_phone}</div>}
                  {rejectModalOffer.buyer_agent_email && <div style={{fontSize: '14px', color: '#374151'}}>{rejectModalOffer.buyer_agent_email}</div>}
                </div>
                <div style={{fontSize: '14px', color: '#374151', lineHeight: '1.5'}}>
                  Contact the buyer's agent to decline this offer. If you'd like to negotiate different terms, consider countering instead.
                </div>
              </div>
            </div>
            <div className="modal-footer-redesigned">
              <button className="btn btn-secondary" onClick={() => setRejectModalOffer(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmRejectOffer} disabled={processingOffer} style={{backgroundColor: '#111827'}}>
                {processingOffer ? 'Processing...' : 'Mark as Rejected'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Review Modal */}
      {showAIReviewModal && selectedListing && (
        <div className="modal-overlay" onClick={() => { setShowAIReviewModal(false); setAiReviewResult(null); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '800px', maxWidth: '90vw', maxHeight: '85vh', borderRadius: '16px' }}>
            <div className="modal-header-redesigned">
              <div className="modal-header-left">
                <div className="modal-icon modal-icon-violet">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                  </svg>
                </div>
                <h3>AI Offer Review</h3>
              </div>
              <button className="modal-close-redesigned" onClick={() => { setShowAIReviewModal(false); setAiReviewResult(null); }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{ padding: '24px', overflowY: 'auto' }}>
              
              {!aiReviewResult && !aiReviewLoading && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#111827' }}>Ready to Analyze Your Offers</h4>
                  <p style={{ color: '#6b7280', marginBottom: '24px' }}>Get AI-powered insights on your offers including rankings, red flags, and counter-offer strategies.</p>
                  <button 
                    className="btn btn-primary" 
                    onClick={handleAIReview}
                    style={{ background: '#111827', padding: '12px 32px', fontSize: '15px' }}
                  >
                    Generate Analysis
                  </button>
                </div>
              )}
              
              {aiReviewLoading && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
                  <p style={{ color: '#6b7280' }}>Analyzing offers and market data...</p>
                </div>
              )}
              
              {aiReviewResult && (
                <div className="ai-review-results">
                  {(() => {
                    let data;
                    try {
                      data = typeof aiReviewResult === 'string' ? JSON.parse(aiReviewResult) : aiReviewResult;
                    } catch (e) {
                      return (
                        <div style={{ padding: '20px', border: '2px solid #111827', borderRadius: '10px', background: 'white' }}>
                          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{aiReviewResult}</pre>
                        </div>
                      );
                    }
                    
                    return (
                      <>
                        {/* 1. Recommendation */}
                        <div style={{
                          border: '2px solid #111827',
                          borderRadius: '10px',
                          padding: '16px 20px',
                          marginBottom: '16px',
                          background: 'white',
                          maxWidth: '600px',
                          margin: '0 auto 16px auto'
                        }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', color: '#6b7280' }}>
                            Recommendation
                          </div>
                          <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: '#111827' }}>
                            {data.recommendation}: {data.recommendationText}
                          </div>
                          <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>
                            {data.summary}
                          </div>
                        </div>

                        {/* 2. Offer Rankings */}
                        {data.offerRankings && data.offerRankings.length > 0 && (
                          <div style={{ border: '2px solid #111827', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', background: 'white', maxWidth: '600px', margin: '0 auto 16px auto' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                              Offer Rankings
                            </div>
                            {data.offerRankings.map((offer, i) => (
                              <div key={i} style={{
                                borderTop: i > 0 ? '1px solid #e5e7eb' : 'none',
                                paddingTop: i > 0 ? '12px' : '0',
                                marginTop: i > 0 ? '12px' : '0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px'
                              }}>
                                <div style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  background: i === 0 ? '#111827' : '#e5e7eb',
                                  color: i === 0 ? 'white' : '#6b7280',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}>
                                  {offer.rank}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <span style={{ fontWeight: 600, color: '#111827' }}>{offer.buyerName}</span>
                                    <span style={{ fontWeight: 600, color: '#111827' }}>${offer.amount?.toLocaleString()}</span>
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                    <span style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      marginRight: '8px',
                                      background: '#111827',
                                      color: 'white'
                                    }}>
                                      {offer.score}
                                    </span>
                                    {offer.pros && offer.pros.length > 0 && <span>+{offer.pros[0]}</span>}
                                    {offer.cons && offer.cons.length > 0 && <span style={{ marginLeft: '8px' }}>−{offer.cons[0]}</span>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* 3. Strengths */}
                        {data.strengths && data.strengths.length > 0 && (
                          <div style={{ border: '2px solid #111827', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', background: 'white', maxWidth: '600px', margin: '0 auto 16px auto' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                              Strengths
                            </div>
                            <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: '14px', color: '#374151' }}>
                              {data.strengths.map((s, i) => <li key={i} style={{ marginBottom: '6px' }}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                        
                        {/* 4. Concerns */}
                        {data.concerns && data.concerns.length > 0 && (
                          <div style={{ border: '2px solid #111827', borderRadius: '10px', padding: '16px 20px', marginBottom: '16px', background: 'white', maxWidth: '600px', margin: '0 auto 16px auto' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                              Concerns
                            </div>
                            <ul style={{ margin: 0, padding: '0 0 0 20px', fontSize: '14px', color: '#374151' }}>
                              {data.concerns.map((c, i) => <li key={i} style={{ marginBottom: '6px' }}>{c}</li>)}
                            </ul>
                          </div>
                        )}

                        {/* 5. Counter Strategy */}
                        {data.counterStrategy && data.counterStrategy.suggestedPrice && (
                          <div style={{
                            border: '2px solid #111827',
                            borderRadius: '10px',
                            padding: '16px 20px',
                            marginBottom: '16px',
                            background: 'white',
                            maxWidth: '600px',
                            margin: '0 auto 16px auto'
                          }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                              Counter Strategy
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '12px' }}>
                              ${data.counterStrategy.suggestedPrice?.toLocaleString()}
                            </div>
                            {data.counterStrategy.suggestedTerms && (
                              <div style={{ fontSize: '14px', color: '#374151', marginBottom: '8px' }}>
                                {data.counterStrategy.suggestedTerms}
                              </div>
                            )}
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>
                              {data.counterStrategy.reasoning}
                            </div>
                          </div>
                        )}

                        {/* Regenerate Button */}
                        <div style={{ textAlign: 'center', marginTop: '16px' }}>
                          <button 
                            className="btn btn-primary" 
                            onClick={handleAIReview}
                            style={{ background: '#111827', padding: '12px 24px', fontSize: '15px' }}
                          >
                            Regenerate Analysis
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Listing Modal */}
      {showEditModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Listing: {selectedListing.title}</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = Object.fromEntries(formData.entries());
                try {
                  const token = localStorage.getItem('token');
                  await axios.put(`/api/listings/${selectedListing.id}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  alert('Listing updated successfully!');
                  setShowEditModal(false);
                  fetchMyListings();
                } catch (error) {
                  console.error('Error updating listing:', error);
                  alert('Failed to update listing');
                }
              }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input type="text" name="title" defaultValue={selectedListing.title} required />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input type="number" name="price" defaultValue={selectedListing.price} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea name="description" defaultValue={selectedListing.description} rows={4} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Address</label>
                    <input type="text" name="address" defaultValue={selectedListing.address} required />
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <input type="text" name="city" defaultValue={selectedListing.city} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>State</label>
                    <input type="text" name="state" defaultValue={selectedListing.state} required />
                  </div>
                  <div className="form-group">
                    <label>ZIP Code</label>
                    <input type="text" name="zipCode" defaultValue={selectedListing.zip_code} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Bedrooms</label>
                    <input type="number" name="bedrooms" defaultValue={selectedListing.bedrooms} />
                  </div>
                  <div className="form-group">
                    <label>Bathrooms</label>
                    <input type="number" name="bathrooms" step="0.5" defaultValue={selectedListing.bathrooms} />
                  </div>
                  <div className="form-group">
                    <label>Square Feet</label>
                    <input type="number" name="squareFeet" defaultValue={selectedListing.square_feet} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select name="status" defaultValue={selectedListing.status || 'active'}>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="sold">Sold</option>
                      <option value="withdrawn">Withdrawn</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content modal-delete" onClick={e => e.stopPropagation()}>
            <div className="delete-modal-content">
              <div className="delete-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </div>
              <h3>Delete Listing</h3>
              <p>Are you sure you want to delete <strong>{selectedListing.title}</strong>? This action cannot be undone.</p>
              <div className="delete-modal-actions">
                <button 
                  className="btn-delete-cancel" 
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-delete-confirm" 
                  onClick={handleDeleteListing}
                  disabled={deletingListing}
                >
                  {deletingListing ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copy Toast Notification */}
      {copyToast && (
        <div style={{
          position: 'fixed',
          top: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#111827',
          color: '#fff',
          borderRadius: '10px',
          padding: '16px 24px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
          zIndex: 9999,
          textAlign: 'center',
          maxWidth: '440px',
          width: '90%',
          animation: 'toastIn 0.25s ease'
        }}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>
            Link copied to clipboard
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af', wordBreak: 'break-all' }}>
            {copyToast}
          </div>
        </div>
      )}

      {/* Footer with contact info */}
      <div className="portal-footer">
        Need help? <a href="tel:206-677-7443">206-677-7443</a> or <a href="mailto:support@liberastro.com">support@liberastro.com</a>
      </div>
    </div>
  );
}

export default SellerPortal;
